import { Paperclip } from "@phosphor-icons/react";
import { type ChangeEvent, useRef } from "react";

import {
  ALLOWED_EXTENSIONS,
  ALLOWED_MIME_TYPES,
} from "features/attachments/constants";

import { Button } from "common/components/ui/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "common/components/ui/Tooltip";

const ACCEPT_STRING = [...ALLOWED_EXTENSIONS, ...ALLOWED_MIME_TYPES].join(",");
const MAX_FILES_LIMIT = 6;

interface UploadAttachmentButtonProps {
  onFileUpload: (file: File) => void;
  currentFileCount: number;
  isDisabled: boolean;
  size: "xs" | "md";
}

export default function UploadAttachmentButton({
  isDisabled,
  onFileUpload,
  currentFileCount,
  size,
}: UploadAttachmentButtonProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleFileSelect(event: ChangeEvent<HTMLInputElement>) {
    const { files } = event.target;
    if (files) {
      for (let i = 0; i < files.length; i++) {
        const file = files.item(i);
        onFileUpload(file);
      }
    }
    const input = event.target as HTMLInputElement;
    input.value = "";
  }

  const isLimitReached = currentFileCount >= MAX_FILES_LIMIT;

  const triggerButton = (
    <Button
      variant="ghost-black"
      size={size}
      intent="iconOnly"
      onClick={() => fileInputRef.current?.click()}
      disabled={isDisabled || isLimitReached}
    >
      <Paperclip />
    </Button>
  );

  return (
    <>
      {isLimitReached ? (
        <Tooltip>
          <TooltipTrigger asChild>{triggerButton}</TooltipTrigger>
          <TooltipContent>
            You&apos;ve added the maximum number of attachments (
            {MAX_FILES_LIMIT})
          </TooltipContent>
        </Tooltip>
      ) : (
        triggerButton
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        multiple
        onChange={handleFileSelect}
        accept={ACCEPT_STRING}
      />
    </>
  );
}
